import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCompanySettings, invalidateCompanySettings } from '@/utils/companySettings';
import { ArrowLeft, Upload, Building2, Palette, FileImage, HelpCircle, Save, User, Home, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { EmailTemplates } from '@/components/EmailTemplates';
import HelpIcon from '@/components/HelpIcon';
import TenantTagline from '@/components/branding/TenantTagline';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { getCurrentUser, isAdminRole } from '@/utils/userRole';

export default function CompanySettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showEmailTemplates, setShowEmailTemplates] = useState(false);
  const logoInputRef = useRef(null);
  const watermarkInputRef = useRef(null);
  const designerPhotoRef = useRef(null);
  const iconInputRef = useRef(null);

  useEffect(() => {
    // Admin-only page protection — uses the canonical context-aware reader
    // so the runtime role-switcher (My Desk) is honored here too.
    const user = getCurrentUser();
    if (!isAdminRole(user.role)) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/dashboard');
      return;
    }
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      setSettings(await getCompanySettings({ force: true }));
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/company-settings`, settings);
      invalidateCompanySettings(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // BR-1 — `type` used to be 'primary' or 'light', and 'light' (this page's
  // dark-background picker) was mapped onto logo_url, overwriting the light
  // logo. The caller now names the slot it means.
  const uploadLogo = async (file, target = 'logo_url') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('target', target);
    
    try {
      const response = await axios.post(`${API_URL}/company-settings/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({
        ...prev,
        [response.data.field]: response.data.url
      }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    }
  };

  const uploadWatermark = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/company-settings/upload-watermark`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({
        ...prev,
        watermark: { ...prev.watermark, image_url: response.data.url, type: 'image' }
      }));
      toast.success('Watermark image uploaded');
    } catch (error) {
      console.error('Error uploading watermark:', error);
      toast.error('Failed to upload watermark');
    }
  };

  const uploadDesignerPhoto = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/company-settings/upload-designer-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({
        ...prev,
        principal_designer_photo_url: response.data.url
      }));
      toast.success('Designer photo uploaded');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    }
  };

  const uploadIcon = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/company-settings/upload-icon`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({
        ...prev,
        icon_url: response.data.url
      }));
      toast.success('Icon uploaded');
    } catch (error) {
      console.error('Error uploading icon:', error);
      toast.error('Failed to upload icon');
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const updateWatermark = (field, value) => {
    setSettings(prev => ({
      ...prev,
      watermark: { ...prev.watermark, [field]: value }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <HelpIcon elementId="company-settings" position="inline" size="md" />
                <h1 className="text-xl font-heading font-medium">Company Settings</h1>
              </div>
              <p className="text-sm text-muted-foreground">Configure branding for all documents</p>
            </div>
          </div>
          <Button onClick={saveSettings} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-8 px-6">
        <Tabs defaultValue="company" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="company" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Company Info
            </TabsTrigger>
            <TabsTrigger value="branding" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Branding
            </TabsTrigger>
            <TabsTrigger value="portal" className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              Portal Settings
            </TabsTrigger>
            <TabsTrigger value="watermark" className="flex items-center gap-2">
              <FileImage className="w-4 h-4" />
              Watermark
            </TabsTrigger>
            <TabsTrigger value="emails" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email Templates
            </TabsTrigger>
          </TabsList>

          {/* Company Info Tab */}
          <TabsContent value="company">
            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
                <CardDescription>
                  This information appears on all generated documents, estimates, and mood boards.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="col-span-2 sm:col-span-1">
                    <Label>Company Name</Label>
                    <Input
                      value={settings?.company_name || ''}
                      onChange={(e) => updateField('company_name', e.target.value)}
                      placeholder="Your company name"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    {/* DESIGN PASS 20 — the tagline moved to the Branding tab,
                        beside the logo and the masthead image, because that is
                        where it is used. One field, one place to edit it. */}
                  </div>
                </div>

                <div>
                  <Label>Address</Label>
                  <Textarea
                    value={settings?.address || ''}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder="123 Main St, Suite 100&#10;City, State 12345"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={settings?.phone || ''}
                      onChange={(e) => updateField('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={settings?.email || ''}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="info@company.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={settings?.website || ''}
                      onChange={(e) => updateField('website', e.target.value)}
                      placeholder="www.company.com"
                    />
                  </div>
                  <div>
                    <Label>License Number</Label>
                    <Input
                      value={settings?.license_number || ''}
                      onChange={(e) => updateField('license_number', e.target.value)}
                      placeholder="License #12345"
                    />
                  </div>
                </div>

                <div>
                  <Label>Document Footer Text</Label>
                  <Input
                    value={settings?.footer_text || ''}
                    onChange={(e) => updateField('footer_text', e.target.value)}
                    placeholder="Product specifications subject to change."
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <Card>
              <CardHeader>
                <CardTitle>Logo & Colors</CardTitle>
                <CardDescription>
                  Upload your company logo and set brand colors used throughout documents.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Logo Upload — the masthead sits in the same grid, DP19 */}
                <div className="grid grid-cols-2 gap-6" data-testid="company-logo-grid">
                  <div>
                    <Label className="mb-2 block">Primary Logo</Label>
                    <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                      {settings?.logo_url ? (
                        <div className="space-y-3">
                          <img 
                            src={settings.logo_url.startsWith('data:') || settings.logo_url.startsWith('http') ? settings.logo_url : `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}`}
                            alt="Company Logo"
                            className="max-h-20 mx-auto object-contain"
                          />
                          <TenantTagline tagline={settings?.tagline} align="center"
                                         testid="logo-preview-tagline" />
                          <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
                            Change Logo
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">PNG, JPG, or SVG</p>
                          <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
                            Upload Logo
                          </Button>
                        </div>
                      )}
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0], 'logo_url')}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Used on light backgrounds</p>
                  </div>

                  <div>
                    <Label className="mb-2 block">Light Logo (Optional)</Label>
                    <div className="border-2 border-dashed border-border rounded-lg p-6 text-center bg-gray-900">
                      {settings?.logo_light_url ? (
                        <div className="space-y-3">
                          <img 
                            src={settings.logo_light_url.startsWith('data:') || settings.logo_light_url.startsWith('http') ? settings.logo_light_url : `${process.env.REACT_APP_BACKEND_URL}${settings.logo_light_url}`}
                            alt="Light Logo"
                            className="max-h-20 mx-auto object-contain"
                          />
                          <Button variant="outline" size="sm" onClick={() => document.getElementById('lightLogoInput')?.click()}>
                            Change
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <Upload className="w-8 h-8 mx-auto text-gray-500" />
                          <p className="text-sm text-gray-500">For dark backgrounds</p>
                          <Button variant="outline" size="sm" onClick={() => document.getElementById('lightLogoInput')?.click()}>
                            Upload
                          </Button>
                        </div>
                      )}
                      <input
                        id="lightLogoInput"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0], 'logo_light_url')}
                      />
                    </div>
                  </div>

                  <div className="col-span-2" data-testid="tenant-tagline-setting">
                    <Label htmlFor="taglineInput" className="mb-2 block">Tagline</Label>
                    <Input
                      id="taglineInput"
                      value={settings?.tagline || ''}
                      onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                      placeholder="Your own line — optional"
                      data-testid="tenant-tagline-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Your words, not ours. It sits under the greeting on My Desk and
                      beside your logo. Leave it empty and nothing shows.
                    </p>
                  </div>

                  {/* DP53 — MastheadImageSetting MOVED to Admin Settings > Branding,
                       which is where the look of the app is set. Do not re-add it here. */}
                </div>

                {/* DP53 — the three brand colour fields that used to sit here are GONE.
                     primary_color, secondary_color and accent_color were editable on THIS
                     page AND on Admin Settings > Branding, writing the same three field
                     names on the same company_settings record with no source stamp. The
                     Branding copy is the survivor and is now labelled "Document colours",
                     which is what they actually do. Do not re-add them here. */}
                <div className="pt-6 border-t space-y-4">
                  <Label className="mb-2 block">Type</Label>
                    
                    {/* Default Font */}
                    <div>
                      <Label className="text-sm font-medium mb-1 block">Default Font</Label>
                      <p className="text-xs text-muted-foreground mb-2">App-wide default font. Can be overridden per item.</p>
                      <select
                        value={settings?.default_font || 'Inter'}
                        onChange={(e) => updateField('default_font', e.target.value)}
                        className="w-full h-10 rounded-md border px-3 text-sm"
                        style={{ fontFamily: settings?.default_font || 'Inter' }}
                      >
                        {['Inter', 'Georgia', 'Merriweather', 'Playfair Display', 'Lora', 'Roboto', 'Open Sans', 'Montserrat', 'Poppins', 'Source Sans Pro', 'Arial', 'Times New Roman', 'Courier New', 'Garamond'].map(f => (
                          <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                        ))}
                      </select>
                    </div>
                </div>

                {/* Document Options */}
                <div className="pt-6 border-t space-y-4">
                  <Label className="mb-2 block">Document Options</Label>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Show Logo on Documents</p>
                      <p className="text-xs text-muted-foreground">Display company logo in document headers</p>
                    </div>
                    <Switch
                      checked={settings?.show_logo_on_documents ?? true}
                      onCheckedChange={(checked) => updateField('show_logo_on_documents', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Show Company Name on Documents</p>
                      <p className="text-xs text-muted-foreground">Display company name in document headers</p>
                    </div>
                    <Switch
                      checked={settings?.show_company_name_on_documents ?? true}
                      onCheckedChange={(checked) => updateField('show_company_name_on_documents', checked)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Portal Settings Tab */}
          <TabsContent value="portal">
            <div className="space-y-6">
              {/* Principal Designer */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Principal Designer
                  </CardTitle>
                  <CardDescription>
                    This information is displayed in the client portal (Drawing Room) and contractor portals.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-start gap-6">
                    {/* Photo Upload - Portrait Rectangle */}
                    <div className="shrink-0">
                      <Label className="mb-2 block">Photo</Label>
                      <div 
                        className="w-28 h-36 rounded-lg bg-muted flex items-center justify-center overflow-hidden cursor-pointer border-2 border-dashed border-border hover:border-primary transition-colors relative group"
                        onClick={() => designerPhotoRef.current?.click()}
                      >
                        {settings?.principal_designer_photo_url ? (
                          <>
                            <img 
                              src={settings.principal_designer_photo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.principal_designer_photo_url}` : settings.principal_designer_photo_url}
                              alt="Designer"
                              className="w-full h-full object-cover object-top"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Upload className="w-6 h-6 text-white" />
                            </div>
                          </>
                        ) : (
                          <div className="text-center">
                            <Upload className="w-6 h-6 mx-auto text-muted-foreground" />
                            <span className="text-xs text-muted-foreground mt-1 block">Upload</span>
                            <span className="text-[10px] text-muted-foreground">Portrait</span>
                          </div>
                        )}
                      </div>
                      <input
                        ref={designerPhotoRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && uploadDesignerPhoto(e.target.files[0])}
                      />
                      <p className="text-xs text-muted-foreground mt-1 text-center">Recommended: 3:4 ratio</p>
                    </div>
                    
                    {/* Name and Title */}
                    <div className="flex-1 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Name</Label>
                          <Input
                            value={settings?.principal_designer_name || ''}
                            onChange={(e) => updateField('principal_designer_name', e.target.value)}
                            placeholder="Your name"
                          />
                        </div>
                        <div>
                          <Label>Title</Label>
                          <Input
                            value={settings?.principal_designer_title || ''}
                            onChange={(e) => updateField('principal_designer_title', e.target.value)}
                            placeholder="Principal Designer"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={settings?.principal_designer_email || ''}
                            onChange={(e) => updateField('principal_designer_email', e.target.value)}
                            placeholder="name@yourcompany.com"
                          />
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input
                            type="tel"
                            value={settings?.principal_designer_phone || ''}
                            onChange={(e) => updateField('principal_designer_phone', e.target.value)}
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Portal Branding */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Home className="w-5 h-5" />
                    Drawing Room Portal <span className="text-xs font-normal text-muted-foreground ml-1">(Client)</span>
                  </CardTitle>
                  <CardDescription>
                    Customize the welcome message and branding for the client portal.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="col-span-2 sm:col-span-1">
                      <Label>Welcome Greeting</Label>
                      <Input
                        value={settings?.portal_welcome_greeting || ''}
                        onChange={(e) => updateField('portal_welcome_greeting', e.target.value)}
                        placeholder="Welcome to The Drawing Room"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Main heading on the Welcome page</p>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <Label>Tagline</Label>
                      <Input
                        value={settings?.portal_tagline || ''}
                        onChange={(e) => updateField('portal_tagline', e.target.value)}
                        placeholder="Your own line — optional"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Shown below the greeting and in the header</p>
                    </div>
                  </div>

                  <div>
                    <Label>Introduction Text</Label>
                    <Textarea
                      value={settings?.portal_intro_text || ''}
                      onChange={(e) => updateField('portal_intro_text', e.target.value)}
                      placeholder="We're excited to begin this creative journey with you..."
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Welcome message paragraph on the portal home page</p>
                  </div>

                  <div>
                    <Label>Signature Line</Label>
                    <Input
                      value={settings?.portal_signature || ''}
                      onChange={(e) => updateField('portal_signature', e.target.value)}
                      placeholder="— Your sign-off"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Sign-off at the end of the welcome message</p>
                  </div>
                </CardContent>
              </Card>

              {/* Site Office - Contractor Portal */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Site Office Portal <span className="text-xs font-normal text-muted-foreground ml-1">(Contractor)</span>
                  </CardTitle>
                  <CardDescription>
                    Customize settings for the contractor portal, including onboarding and insurance requirements.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="col-span-2 sm:col-span-1">
                      <Label>Welcome Greeting</Label>
                      <Input
                        value={settings?.contractor_portal_greeting || ''}
                        onChange={(e) => updateField('contractor_portal_greeting', e.target.value)}
                        placeholder="Welcome to The Site Office"
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <Label>Tagline</Label>
                      <Input
                        value={settings?.contractor_portal_tagline || ''}
                        onChange={(e) => updateField('contractor_portal_tagline', e.target.value)}
                        placeholder="Let's Build Something"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Introduction Text</Label>
                    <Textarea
                      value={settings?.contractor_portal_intro || ''}
                      onChange={(e) => updateField('contractor_portal_intro', e.target.value)}
                      placeholder="Welcome to our contractor portal..."
                      rows={3}
                    />
                  </div>

                  {/* Insurance Submission Info */}
                  <div className="pt-4 border-t">
                    <Label className="text-sm font-semibold block mb-3">Insurance Submission Information</Label>
                    <p className="text-xs text-muted-foreground mb-4">This information is displayed to contractors so they know where to send their insurance documents.</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Contact Email</Label>
                        <Input
                          value={settings?.insurance_submission_info?.email || ''}
                          onChange={(e) => {
                            const info = { ...(settings?.insurance_submission_info || {}), email: e.target.value };
                            updateField('insurance_submission_info', info);
                          }}
                          placeholder="insurance@yourcompany.com"
                        />
                      </div>
                      <div>
                        <Label>Attention</Label>
                        <Input
                          value={settings?.insurance_submission_info?.attention || ''}
                          onChange={(e) => {
                            const info = { ...(settings?.insurance_submission_info || {}), attention: e.target.value };
                            updateField('insurance_submission_info', info);
                          }}
                          placeholder="e.g., your principal designer"
                        />
                      </div>
                      <div>
                        <Label>Mailing Address</Label>
                        <Input
                          value={settings?.insurance_submission_info?.address || ''}
                          onChange={(e) => {
                            const info = { ...(settings?.insurance_submission_info || {}), address: e.target.value };
                            updateField('insurance_submission_info', info);
                          }}
                          placeholder="Springfield, MO"
                        />
                      </div>
                      <div>
                        <Label>Additional Notes</Label>
                        <Input
                          value={settings?.insurance_submission_info?.notes || ''}
                          onChange={(e) => {
                            const info = { ...(settings?.insurance_submission_info || {}), notes: e.target.value };
                            updateField('insurance_submission_info', info);
                          }}
                          placeholder="e.g., Name us as Additional Insured"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Icon Upload */}
              <Card>
                <CardHeader>
                  <CardTitle>Portal Icons</CardTitle>
                  <CardDescription>
                    Upload a single icon version of your logo for use in portal headers and footers.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6">
                    <div 
                      className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center overflow-hidden cursor-pointer border-2 border-dashed border-border hover:border-primary transition-colors"
                      onClick={() => iconInputRef.current?.click()}
                    >
                      {settings?.icon_url ? (
                        <img 
                          src={settings.icon_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.icon_url}` : settings.icon_url}
                          alt="Icon"
                          className="w-full h-full object-contain p-2"
                        />
                      ) : (
                        <div className="text-center">
                          <Upload className="w-6 h-6 mx-auto text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Upload</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium">Single Icon</p>
                      <p className="text-xs text-muted-foreground">Used in portal header and footer. Square format recommended.</p>
                    </div>
                    <input
                      ref={iconInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && uploadIcon(e.target.files[0])}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Legal Links */}
              <Card>
                <CardHeader>
                  <CardTitle>Legal & Help Links</CardTitle>
                  <CardDescription>
                    These pages are built into the portal and accessible to all users. Click to preview.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Help URL</Label>
                    <Input
                      value={settings?.help_url || ''}
                      onChange={(e) => updateField('help_url', e.target.value)}
                      placeholder="https://yoursite.com/help"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Link to your help documentation or FAQ page</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg border bg-muted/30 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Privacy Policy</p>
                        <p className="text-xs text-muted-foreground">Auto-generated with your company info</p>
                      </div>
                      <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-[#BC5D41] text-sm hover:underline font-medium">Preview</a>
                    </div>
                    <div className="p-4 rounded-lg border bg-muted/30 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Terms of Service</p>
                        <p className="text-xs text-muted-foreground">Auto-generated with your company info</p>
                      </div>
                      <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="text-[#BC5D41] text-sm hover:underline font-medium">Preview</a>
                    </div>
                  </div>
                  <div>
                    <Label>Copyright Text</Label>
                    <Input
                      value={settings?.copyright_text || ''}
                      onChange={(e) => updateField('copyright_text', e.target.value)}
                      placeholder="© 2026 Your Company Name. All rights reserved."
                    />
                    <p className="text-xs text-muted-foreground mt-1">Copyright notice for printed documents and portal footer</p>
                  </div>
                </CardContent>
              </Card>

              {/* Live Preview Card */}
              <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileImage className="w-5 h-5 text-primary" />
                    Live Preview
                  </CardTitle>
                  <CardDescription>
                    See how your settings will appear in the client portal
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-[#F5F2ED] rounded-lg border border-[#D5D0C8] shadow-sm p-6 space-y-4">
                    {/* Header Preview */}
                    <div className="flex items-center justify-between border-b border-[#D5D0C8] pb-4">
                      <div className="flex items-center gap-3">
                        {settings?.icon_url ? (
                          <img 
                            src={settings.icon_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.icon_url}` : settings.icon_url}
                            alt="Icon"
                            className="w-10 h-10 object-contain"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-[#D5D0C8] rounded flex items-center justify-center text-xs text-[#6B6560]">Logo</div>
                        )}
                        <div>
                          <p className="font-semibold text-lg text-[#2C2925]">{settings?.company_name || 'Your Company'}</p>
                          <p className="text-sm text-[#6B6560]">{settings?.portal_tagline || 'Your Tagline'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Content Preview */}
                    <div className="flex gap-6">
                      {/* Designer Photo */}
                      <div className="shrink-0">
                        {settings?.principal_designer_photo_url ? (
                          <img 
                            src={settings.principal_designer_photo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.principal_designer_photo_url}` : settings.principal_designer_photo_url}
                            alt="Designer"
                            className="w-20 h-24 rounded-lg object-cover object-top border border-[#D5D0C8]"
                          />
                        ) : (
                          <div className="w-20 h-24 bg-[#D5D0C8] rounded-lg flex items-center justify-center text-xs text-[#6B6560] border border-[#C5C0B8]">
                            Photo
                          </div>
                        )}
                      </div>
                      {/* Welcome Message */}
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1 text-[#2C2925]">{settings?.portal_welcome_greeting || 'Welcome to The Drawing Room'}</h3>
                        <p className="text-sm text-[#4A4541] mb-2">{settings?.portal_intro_text || 'Your introduction message will appear here...'}</p>
                        <p className="text-sm italic text-[#6B6560]">{settings?.portal_signature || '— Your Team'}</p>
                      </div>
                    </div>

                    {/* Designer Info */}
                    <div className="pt-4 border-t border-[#D5D0C8]">
                      <p className="text-sm font-medium text-[#2C2925]">{settings?.principal_designer_name || 'Designer Name'}</p>
                      <p className="text-xs text-[#6B6560]">{settings?.principal_designer_title || 'Title'}</p>
                      <p className="text-xs text-[#6B6560]">{settings?.principal_designer_email || settings?.email || 'email@example.com'}</p>
                    </div>

                    {/* Footer Preview */}
                    <div className="pt-3 border-t border-[#D5D0C8] flex items-center justify-between text-xs text-[#6B6560]">
                      <span>{settings?.copyright_text || `\u00A9 2026 ${settings?.company_name || 'Your Company'}. All rights reserved.`}</span>
                      <div className="flex gap-3">
                        <a href="/legal/privacy" className="text-[#BC5D41] hover:underline">Privacy</a>
                        <a href="/legal/terms" className="text-[#BC5D41] hover:underline">Terms</a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Watermark Tab */}
          <TabsContent value="watermark">
            <Card>
              <CardHeader>
                <CardTitle>Watermark Settings</CardTitle>
                <CardDescription>
                  Configure watermark for mood boards and documents. Can be text or an image.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Enable Watermark</p>
                    <p className="text-xs text-muted-foreground">Show watermark on mood boards by default</p>
                  </div>
                  <Switch
                    checked={settings?.watermark?.enabled ?? false}
                    onCheckedChange={(checked) => updateWatermark('enabled', checked)}
                  />
                </div>

                <div className="pt-4 border-t">
                  <Label className="mb-2 block">Watermark Type</Label>
                  <Select
                    value={settings?.watermark?.type || 'text'}
                    onValueChange={(v) => updateWatermark('type', v)}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="image">Image</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {settings?.watermark?.type === 'text' ? (
                  <div>
                    <Label>Watermark Text</Label>
                    <Input
                      value={settings?.watermark?.text || ''}
                      onChange={(e) => updateWatermark('text', e.target.value)}
                      placeholder="DRAFT"
                    />
                  </div>
                ) : (
                  <div>
                    <Label className="mb-2 block">Watermark Image</Label>
                    <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                      {settings?.watermark?.image_url ? (
                        <div className="space-y-3">
                          <img 
                            src={settings.watermark.image_url.startsWith('data:') || settings.watermark.image_url.startsWith('http') ? settings.watermark.image_url : `${process.env.REACT_APP_BACKEND_URL}${settings.watermark.image_url}`}
                            alt="Watermark"
                            className="max-h-20 mx-auto object-contain opacity-50"
                          />
                          <Button variant="outline" size="sm" onClick={() => watermarkInputRef.current?.click()}>
                            Change Image
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <FileImage className="w-8 h-8 mx-auto text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">Upload watermark image</p>
                          <Button variant="outline" size="sm" onClick={() => watermarkInputRef.current?.click()}>
                            Upload
                          </Button>
                        </div>
                      )}
                      <input
                        ref={watermarkInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && uploadWatermark(e.target.files[0])}
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label>Size (px)</Label>
                    <div className="flex items-center gap-3">
                      <Slider
                        value={[settings?.watermark?.size || 200]}
                        onValueChange={([v]) => updateWatermark('size', v)}
                        min={50}
                        max={500}
                        step={10}
                        className="flex-1"
                      />
                      <span className="text-sm w-12 text-right">{settings?.watermark?.size || 200}</span>
                    </div>
                  </div>
                  <div>
                    <Label>Rotation (°)</Label>
                    <div className="flex items-center gap-3">
                      <Slider
                        value={[settings?.watermark?.rotation || -30]}
                        onValueChange={([v]) => updateWatermark('rotation', v)}
                        min={-90}
                        max={90}
                        step={5}
                        className="flex-1"
                      />
                      <span className="text-sm w-12 text-right">{settings?.watermark?.rotation || -30}°</span>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Opacity</Label>
                  <div className="flex items-center gap-3">
                    <Slider
                      value={[(settings?.watermark?.opacity || 0.15) * 100]}
                      onValueChange={([v]) => updateWatermark('opacity', v / 100)}
                      min={5}
                      max={50}
                      step={5}
                      className="flex-1"
                    />
                    <span className="text-sm w-12 text-right">{Math.round((settings?.watermark?.opacity || 0.15) * 100)}%</span>
                  </div>
                </div>

                {/* Preview */}
                <div className="pt-6 border-t">
                  <Label className="mb-3 block">Preview</Label>
                  <div className="relative bg-white border rounded-lg h-48 flex items-center justify-center overflow-hidden">
                    <p className="text-muted-foreground text-sm">Sample document content</p>
                    {settings?.watermark?.enabled && (
                      <div 
                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                        style={{
                          opacity: settings.watermark.opacity || 0.15,
                        }}
                      >
                        {settings.watermark.type === 'text' ? (
                          <span 
                            className="text-gray-500 font-bold select-none"
                            style={{
                              fontSize: `${(settings.watermark.size || 200) / 5}px`,
                              transform: `rotate(${settings.watermark.rotation || -30}deg)`,
                            }}
                          >
                            {settings.watermark.text || 'DRAFT'}
                          </span>
                        ) : settings.watermark.image_url ? (
                          <img
                            src={settings.watermark.image_url.startsWith('data:') || settings.watermark.image_url.startsWith('http') ? settings.watermark.image_url : `${process.env.REACT_APP_BACKEND_URL}${settings.watermark.image_url}`}
                            alt="Watermark preview"
                            style={{
                              width: settings.watermark.size || 200,
                              transform: `rotate(${settings.watermark.rotation || -30}deg)`,
                            }}
                          />
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="emails">
            <Card>
              <CardHeader>
                <CardTitle>Email Templates</CardTitle>
                <CardDescription>
                  Manage email templates for change orders, milestones, approvals, and more.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Email templates help you maintain consistent, professional communication with clients and contractors.
                    Use variables like <code className="bg-gray-100 px-1 rounded">{'{{client_name}}'}</code> for personalization.
                  </p>
                  <Button onClick={() => setShowEmailTemplates(true)} className="bg-[#BC5D41] hover:bg-[#70170F]">
                    <Mail className="w-4 h-4 mr-2" />
                    Manage Email Templates
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Email Templates Dialog */}
        <EmailTemplates
          isOpen={showEmailTemplates}
          onClose={() => setShowEmailTemplates(false)}
          darkMode={false}
        />

        {/* Help Note */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg flex items-start gap-3">
          <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">About KEY-MADE Branding</p>
            <p className="mt-1">
              KEY-MADE is the application name and will always appear on documents. Your company 
              information (name, logo) appears alongside KEY-MADE as the primary branding.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
